import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { mapLinks, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import XTwitter from "@insite/mobius/Icons/XTwitter";
import Link from "@insite/mobius/Link";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Facebook from "@ucs/Icons/Facebook";
import InstagramFilled from "@ucs/Icons/InstagramFilled";
import LinkedIn from "@ucs/Icons/LinkedIn";
import Newsletter from "@ucs/Icons/Newsletter";
import Twitter from "@ucs/Icons/Twitter";
import Youtube from "@ucs/Icons/Youtube";
import UCSColors from "@ucs/Utils/UCSColors";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    links = "links",
    direction = "direction",
    alignment = "alignment",
    opacity = "opacity",
    backgroundColor = "backgroundColor",
    hoverColor = "hoverColor",
    invertDirection = "invertDirection",
    customCSS = "customCSS",
}

const enum icons {
    instagram = "Instagram",
    facebook = "Facebook",
    youtube = "Youtube",
    twitter = "Twitter",
    xTwitter = "X",
    linkedin = "LinkedIn",
    newsletter = "Newsletter",
}

const enum alignment {
    left = "left",
    center = "center",
    right = "right",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.direction]: "vertical" | "horizontal";
        [fields.alignment]: "left" | "center" | "right";
        [fields.opacity]: number;
        [fields.backgroundColor]: string;
        [fields.hoverColor]: string;
        [fields.links]: LinkModel[];
        [fields.invertDirection]: boolean;
        [fields.customCSS]: string;
    };
}

interface LinkModel {
    fields: {
        title: string;
        icon: string;
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        overriddenTitle: string;
    };
}

export interface LinkListStyles {
    linksHorizontalWrapper?: InjectableCss;
    linksVerticalWrapper?: InjectableCss;
}

export const linkListStyles: LinkListStyles = {
    linksHorizontalWrapper: {
        css: css`
            width: 100%;
            padding: 0 0;
            display: flex;
            flex-direction: row;
            align-items: flex-start;
            & > * {
                &:not(:first-child) {
                    margin-left: 16px;
                }
            }
        `,
    },
    linksVerticalWrapper: {
        css: css`
            width: 100%;
            padding: 0;
            display: flex;
            flex-direction: column;
            & > * {
                &:not(:first-child) {
                    margin-top: 16px;
                }
            }
        `,
    },
};

export const socialLinksStyles = linkListStyles;

const styles = linkListStyles;

const GetIcon = (icon: string) => {
    switch (icon) {
        case icons.youtube:
            return Youtube;
        case icons.twitter:
            return Twitter;
        case icons.xTwitter:
            return XTwitter;
        case icons.facebook:
            return Facebook;
        case icons.instagram:
            return InstagramFilled;
        case icons.linkedin:
            return LinkedIn;
        case icons.newsletter:
            return Newsletter;
        default:
            break;
    }

    return "";
};

const getAlignment = (align: string) => {
    switch (align) {
        case alignment.left:
            return "flex-start";
        case alignment.center:
            return "center";
        case alignment.right:
            return "flex-end";
        default:
            break;
    }
    return "";
};

interface OptionalFieldReturn {
    title: string;
    icon: string;
    openInNewWindow: boolean;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const links = mapLinks<LinkModel, OptionalFieldReturn>(state, ownProps.fields.links, (widgetLink, stateLink) => ({
        title: widgetLink.fields.title ?? stateLink?.title,
        icon: widgetLink.fields.icon,
        openInNewWindow: widgetLink.fields.openInNewWindow,
    }));
    return {
        links,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const SocialLinks: FC<Props> = ({ fields, links }) => {
    const { hasMissingCategories } = useGetLinks(fields.links, o => o.fields.destination);

    const socialLinkListStyle = css`
        justify-content: ${getAlignment(fields.alignment)};
        align-items: ${getAlignment(fields.alignment)};
        ${fields.direction === "horizontal" ? styles.linksHorizontalWrapper?.css : styles.linksVerticalWrapper?.css}
        ${({ theme }) => {
            return breakpointMediaQueries(theme, [
                css`
                    ${fields.invertDirection && fields.direction === "horizontal"
                        ? styles.linksVerticalWrapper?.css
                        : styles.linksHorizontalWrapper?.css}
                    & > * {
                        &:not(:first-child) {
                            margin-top: 0;
                        }
                    }
                `,
            ]);
        }}
    `;

    const linkStyle = {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            border-radius: 50px;
            background-color: ${fields.backgroundColor};
            opacity: ${fields.opacity / 100};

            &:hover {
                background-color: ${fields.hoverColor};
                color: ${UCSColors.gray10} !important;
            }
        `,
    };

    if (hasMissingCategories) {
        return null;
    }

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };
    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="social-links" css={socialLinkListStyle}>
                {links.map((link, index) => (
                    <StyledWrapper className="link-wrapper" key={index}>
                        {link?.url && (
                            <Link
                                href={link.url}
                                target={link.openInNewWindow ? "_blank" : ""}
                                icon={{
                                    iconProps: {
                                        color: `${UCSColors.gray10}`,
                                        src: GetIcon(link.icon) as string,
                                        size: 20,
                                        css: css`
                                            margin-right: 0;
                                            &:hover {
                                                color: ${UCSColors.gray10} !important;
                                            }
                                        `,
                                        title: fields.links[index].fields.title,
                                    },
                                }}
                                {...linkStyle}
                                hoverStyle={{ color: `${UCSColors.gray10} !important` }}
                            ></Link>
                        )}
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.social-links{
}

.link-wrapper{
}

.social-link{
}`;

const contentTab = { displayName: "Content", sortOrder: 1 };

const settingsTab = {
    displayName: "Settings",
    sortOrder: 2,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 3,
};

const definition: WidgetDefinition = {
    group: "Basic",
    icon: "thumbs-up",
    isSystem: true,
    fieldDefinitions: [
        {
            name: fields.links,
            editorTemplate: "ListField",
            tab: contentTab,
            getDisplay: (item: HasFields) => {
                return item.fields.title;
            },
            defaultValue: [],
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    name: "title",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: "icon",
                    displayName: "Select Icon",
                    editorTemplate: "DropDownField",
                    options: [
                        { displayName: "Instagram", value: icons.instagram },
                        { displayName: "Youtube", value: icons.youtube },
                        { displayName: "Facebook", value: icons.facebook },
                        { displayName: "Twitter", value: icons.twitter },
                        { displayName: "X", value: icons.xTwitter },
                        { displayName: "LinkedIn", value: icons.linkedin },
                        { displayName: "Newsletter", value: icons.newsletter },
                    ],
                    defaultValue: "",
                    isRequired: true,
                },
                {
                    name: "destination",
                    editorTemplate: "LinkField",
                    defaultValue: {
                        value: "",
                        type: "Url",
                    },
                    isRequired: true,
                },
                {
                    name: "openInNewWindow",
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                },
            ],
        },
        {
            name: fields.direction,
            editorTemplate: "DropDownField",
            defaultValue: "vertical",
            fieldType: "General",
            options: [
                {
                    displayName: "Vertical",
                    value: "vertical",
                },
                {
                    displayName: "Horizontal",
                    value: "horizontal",
                },
            ],
            hideEmptyOption: true,
            sortOrder: 0,
            tab: settingsTab,
        },
        {
            name: fields.alignment,
            editorTemplate: "DropDownField",
            defaultValue: alignment.center,
            fieldType: "General",
            options: [
                {
                    displayName: "Left",
                    value: alignment.left,
                },
                {
                    displayName: "Center",
                    value: alignment.center,
                },
                {
                    displayName: "Right",
                    value: alignment.right,
                },
            ],
            hideEmptyOption: true,
            tab: settingsTab,
            sortOrder: 1,
        },
        {
            name: fields.opacity,
            displayName: "Opacity",
            editorTemplate: "IntegerField",
            defaultValue: 100,
            min: 1,
            max: 100,
            fieldType: "General",
            sortOrder: 2,
            tab: settingsTab,
        },
        {
            name: fields.backgroundColor,
            displayName: "Background Color",
            editorTemplate: "ColorPickerField",
            defaultValue: `${UCSColors.gray03}`,
            fieldType: "General",
            sortOrder: 3,
            tab: settingsTab,
        },
        {
            name: fields.hoverColor,
            displayName: "Hover Color",
            editorTemplate: "ColorPickerField",
            defaultValue: `${UCSColors.gray04}`,
            fieldType: "General",
            sortOrder: 4,
            tab: settingsTab,
        },
        {
            name: fields.customCSS,
            displayName: "Custom CSS",
            editorTemplate: "CodeField",
            fieldType: "General",
            tab: advancedTab,
            defaultValue: defaultCustomCss,
            isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
            validate: value => {
                if (value === undefined || value === null) {
                    return "";
                }

                const result = cssLinter.parse(value, { silent: true });
                if (result?.stylesheet?.parsingErrors) {
                    // the error output at this time only has room for one line so we just show the first error
                    return result.stylesheet.parsingErrors.length <= 0
                        ? ""
                        : result.stylesheet.parsingErrors.map(
                              (error: any) => `${error.reason} on line ${error.line}`,
                          )[0];
                }

                return "Unable to parse Css";
            },
            options: {
                mode: "css",
                lint: true,
                autoRefresh: true,
            },
        },
        {
            name: fields.invertDirection,
            displayName: "Invert Direction on Mobile",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
            sortOrder: 5,
            tab: settingsTab,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(SocialLinks),
    definition,
};

export default widgetModule;
